<template>
  <div class="contenedor-graf">
    <h3 class="titulo">
      <span
        class="hHombres"
        @mouseover="highlight('homicidio_hombres')"
        @mouseleave="noHighlight('homicidio_hombres')"
        >Homicidio de hombres</span
      >,
      <span
        class="hMujeres"
        @mouseover="highlight('homicidio_mujeres')"
        @mouseleave="noHighlight('homicidio_mujeres')"
        >homicidio de mujeres</span
      >,
      <span
        class="dHombres"
        @mouseover="highlight('desaparicion_hombres')"
        @mouseleave="noHighlight('desaparicion_hombres')"
        >desaparición de hombres</span
      >
      y
      <span
        class="dMujeres"
        @mouseover="highlight('desaparicion_mujeres')"
        @mouseleave="noHighlight('desaparicion_mujeres')"
        >desaparición de mujeres</span
      >
    </h3>
    <text class="instrucciones">
      Coloca el cursor sobre el texto de color para resaltar esa opción
    </text>
    <svg
      id="areas-tiempo"
      :width="ancho + margenes.derecha + margenes.izquierda"
      :height="alto_svg"
    >
      <g
        class="eje-x"
        :transform="`translate(${margenes.izquierda},${
          alto + margenes.arriba
        })`"
      ></g>
      <g
        class="eje-y"
        :transform="`translate(${margenes.izquierda},${margenes.arriba})`"
      ></g>
      <g
        class="grupo-areas"
        :transform="`translate(${margenes.izquierda},${margenes.arriba})`"
      ></g>
    </svg>
  </div>
</template>

<script setup>
import * as d3 from "d3";
import { onMounted, ref, toRefs, watch, onUnmounted } from "vue";

const categorias = [
  "desaparicion_hombres",
  "desaparicion_mujeres",
  "homicidio_hombres",
  "homicidio_mujeres",
];
const dict_color = {
  desaparicion_hombres: "#ED594A",
  desaparicion_mujeres: "#8B7693",
  homicidio_hombres: "#6A9297",
  homicidio_mujeres: "#DEC181",
};

// eslint-disable-next-line
const props = defineProps({
  datos: {
    type: Array,
    default: Array,
  },
});
const { datos } = toRefs(props);
const datos_formateados = ref();

const contenedor_svg = ref();
const svg = ref();
const ancho = ref(0);
const alto_svg = ref(400);
const alto = ref(0);
const margenes = ref({
  derecha: 20,
  izquierda: 50,
  abajo: 35,
  arriba: 20,
});

const formatoTemporal = ref(d3.timeParse("%Y"));
const escalaTemporal = ref(d3.scaleTime());
const escalaLineal = ref(d3.scaleLinear());
const eje_x = ref();
const eje_y = ref();
const texto_x = ref();
const texto_y = ref();
const grupo_areas = ref();

onMounted(() => {
  contenedor_svg.value = document.querySelector(".contenedor-graf");

  svg.value = d3.select("svg#areas-tiempo");
  grupo_areas.value = svg.value.select("g.grupo-areas");

  eje_x.value = svg.value.select("g.eje-x");
  eje_y.value = svg.value.select("g.eje-y");
  texto_x.value = svg.value.append("text").attr("text-anchor", "end");
  texto_y.value = svg.value.append("text").attr("text-anchor", "end");

  /**
   * Siguiendo con la idea de una data formateada, queremos un path por categoría
   * tiene el formato
   * [
   * {
   *   categoria: "...",
   *   los_datos: [
   *     {
   *       fecha:...,
   *       valor: ...
   *     },
   *     {...}
   *     ...
   *     ]
   *   },
   *   {...},
   *   {...},
   *   {...}
   * ]
   *
   *
   */
  datos_formateados.value = categorias.map((cat) => ({
    categoria: cat,
    los_datos: datos.value.map((d) => ({
      fecha: formatoTemporal.value(d.anio),
      valor: d[cat],
    })),
  }));

  calcularDimensiones();
  graficaAreas();
  // Agregamos un escuchador de eventos que dispare una función llamada reescalanding al resizear la pantalla
  window.addEventListener("resize", reescalanding);
});
// nomás calcula nuevos tamaños de svg, escalas y pinta de nuevo
function reescalanding() {
  calcularDimensiones();
  graficaAreas();
}
// importante eliminar ese escuchador de ventos al desmontar el componente
onUnmounted(() => {
  window.removeEventListener("resize", reescalanding);
});

watch(datos, () => {
  // Se actualizan los datos formateados
  //console.log(datos);
  datos_formateados.value = categorias.map((cat) => ({
    categoria: cat,
    los_datos: datos.value.map((d) => ({
      fecha: formatoTemporal.value(d.anio),
      valor: d[cat],
    })),
  }));

  // Agregamos estas dons funciones
  calcularDimensiones();
  graficaAreas();
});

function calcularDimensiones() {
  ancho.value =
    contenedor_svg.value.clientWidth -
    margenes.value.derecha -
    margenes.value.izquierda;

  alto.value = alto_svg.value - margenes.value.arriba - margenes.value.abajo;

  escalaTemporal.value
    .domain(d3.extent(datos.value.map((d) => formatoTemporal.value(d.anio))))
    .range([0, ancho.value]);

  escalaLineal.value
    .domain([
      0,
      d3.max(
        datos.value.map((objeto) =>
          d3.max(categorias.map((cat) => objeto[cat]))
        )
      ),
    ])
    .range([alto.value, 0]);

  eje_x.value.call(d3.axisBottom(escalaTemporal.value));
  eje_y.value.call(d3.axisLeft(escalaLineal.value));

  texto_x.value
    .attr("x", ancho.value / 2 + margenes.value.izquierda)
    .attr("y", alto.value + margenes.value.arriba + margenes.value.abajo)
    .text("Año")
    .attr("font-size", "14")
    .attr("font-weight", "bold");

  texto_y.value
    .attr("transform", "rotate(-90)")
    .attr("y", 13)
    .attr("x", -85)
    .attr("font-size", "16")
    .text("Tasa por cada cien mil jóvenes")
    .attr("font-weight", "bold");
}
function graficaAreas() {
  grupo_areas.value
    .selectAll("path") // Acá la selección estaba vacía. Hay que hacer referencia al
    // elemento que vamos a seleccionar (path).

    .data(datos_formateados.value) // Acá metemos los datos formateados, en este caso un arreglo de 4 objetos.
    // cada uno de esos objetos tiene una clave los_datos que tiene como valor la serie de tiempo
    .join(
      (enter) => {
        enter
          .append("path")
          .attr("d", (d) => {
            //console.log(d);
            return d3
              .area()
              .x((dd) => escalaTemporal.value(dd.fecha))
              .y0(escalaLineal.value(0))
              .y1((dd) => escalaLineal.value(dd.valor))(d.los_datos);
          })
          .attr("class", (d) => d.categoria)
          .attr("fill-opacity", ".4")
          .attr("fill", (d) => dict_color[d.categoria]);
      },
      (update) => {
        update
          .transition()
          .attr("d", (d) => {
            return d3
              .area()
              .x((dd) => escalaTemporal.value(dd.fecha))
              .y0(escalaLineal.value(0))
              .y1((dd) => escalaLineal.value(dd.valor))(d.los_datos);
          })
          .attr("fill-opacity", ".4")
          .attr("fill", (d) => dict_color[d.categoria]);
      },
      (exit) => {
        exit.remove();
      }
    );
}

// Highlight when hovered
function highlight(d) {
  //console.log(d);
  // reduce opacity of all groups
  d3.selectAll("path").style("fill-opacity", 0.09);
  // expect the one that is hovered
  d3.select("." + d).style("fill-opacity", 1);
}

// And when it is not hovered anymore
function noHighlight() {
  d3.selectAll("path").style("fill-opacity", 0.4);
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.titulo {
  margin-bottom: 0;
  padding-bottom: 5px;
  color: black;
}
.instrucciones {
  font-size: 14px;
  color: black;
  font-weight: bold;
}
.dHombres {
  color: #ed594a;
  width: 100px;
}
.dMujeres {
  color: #8b7693;
  width: 100px;
}
.hHombres {
  color: #6a9297;
  width: 100px;
}
.hMujeres {
  color: #dec181;
  width: 100px;
}
</style>
